import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';

import { isSaudi } from 'constants/helper';
import { LOG_ACTIONS, PAYMENT_METHODS } from 'constants/packaging';
import { getMaterialLogs } from 'services/packaging';
import { getCurrency } from 'common/countries/countries-mapping';
import { fmt } from 'IntlWrapper/IntlWrapper';
import { COUNTRY_CURRENCY } from 'constants/helper';

import { notify } from 'components/Notify/Notify';

import { ReactComponent as CodIcon } from 'assets/bosta-icons/bank-note-01.svg';
import { ReactComponent as WalletIcon } from 'assets/bosta-icons/wallet-02.svg';
import { ReactComponent as RefundIcon } from 'assets/bosta-icons/refund-packaging-icon.svg';
import { ReactComponent as BostaCreditsIcon } from 'assets/bosta-icons/coins-stacked.svg';
import { ReactComponent as TooltipIcon } from 'assets/bosta-icons/Tooltip.svg';
import { ReactComponent as GlobeIcon } from 'assets/bosta-icons/globe-02.svg';

export const formatKeyName = (title) => {
  return title
    ?.toLowerCase()
    ?.replace(' - ', '_')
    ?.replace(/ /g, '_')
    ?.replace(/-/g, '_')
    ?.replace(/\./g, '_');
};

export const formatMaterialItems = (materialsArray = []) => {
  const formattedMaterials = [];
  materialsArray.forEach((item) => {
    formattedMaterials.push({
      material: {
        name: item.title,
        size: item.size ? `Size ${item.size}` : item.pack ? item.pack : '',
        description: item.description ? item.description : ''
      },
      quantity: item.total_balance,
      price: item.price,
      status: !!item.avalible,
      outOfStock: !!item.out_of_stock,
      id: item.id,
      free: !!item.free,
      maxLimit: item.exclusive_max_limit_per_request,
      isMaxLimitEnabled: !!item.is_exclusive_max_limited_item
    });
  });
  return formattedMaterials;
};

const formatTakenBy = (log) => {
  let userRole = log.user_role?.toLowerCase()?.replaceAll('_', ' ');
  userRole = userRole?.split(' ');
  if (userRole?.length > 1) {
    userRole = userRole.map(
      (name) => name.charAt(0).toUpperCase() + name.slice(1)
    );
    userRole = userRole.join(' ');
  } else {
    userRole = userRole
      ? userRole[0].charAt(0).toUpperCase() + userRole[0].slice(1)
      : '';
  }
  return `${userRole} ${log.user_name}`;
};

export const formatPackagingOrderAmountIcon = (draftInfo) => {
  const { totalCost, totalPrice, paymentMethod } = draftInfo || {};
  const amount = totalCost || totalPrice || 0;

  if (Math.sign(amount) === -1) {
    return {
      text: fmt({
        id: 'orders.shipment_details.shipment_details_summary.table_cells_labels.refund'
      }),
      icon: <RefundIcon />
    };
  }

  switch (paymentMethod) {
    case PAYMENT_METHODS.WALLET:
      return {
        text: fmt({
          id: 'orders.shipment_details.shipment_details_summary.table_cells_labels.bosta_wallet'
        }),
        icon: <WalletIcon className="br-wallet-icon" />,
        tooltip_text: fmt({
          id: 'packaging_wallet.tooltip.message'
        })
      };
    case PAYMENT_METHODS.COD:
      return {
        text: fmt({
          id: 'orders.shipment_details.shipment_details_summary.table_cells_labels.cash_on_delivery'
        }),
        icon: <CodIcon />
      };
    case PAYMENT_METHODS.BOSTA_CREDITS:
      return {
        text: fmt({
          id: 'orders.shipment_details.shipment_details_summary.table_cells_labels.bosta_credits'
        }),
        icon: <BostaCreditsIcon className="br-bosta-credits-icon" />,
        tooltip_text: fmt({
          id: 'packaging_wallet.credits_tooltip'
        })
      };
    case PAYMENT_METHODS.PAID_ONLINE:
      return {
        text: fmt({
          id: 'orders.shipment_details.shipment_details_summary.table_cells_labels.paid_online'
        }),
        icon: <GlobeIcon className="br-paid-online-icon" />,
        tooltip_text: fmt({
          id: 'packaging_wallet.online_tooltip'
        })
      };
  }
};

export const formatAmountColumn = (draftInfo) => {
  const { icon, text, tooltip_text } =
    formatPackagingOrderAmountIcon(draftInfo) || {};

  const { totalCost, totalPrice } = draftInfo || {};

  return totalCost || totalPrice ? (
    <div className="br-amount-column__container">
      {icon}
      <div className="br-amount-column__info-container">
        <span className="body-medium">
          {fmt(
            {
              id: COUNTRY_CURRENCY
            },
            {
              cod: totalCost || totalPrice || 0
            }
          )}
        </span>

        <div className="br-amount-column__info-details">
          <span className="caption-lg">{text}</span>
          {tooltip_text && (
            <Tooltip
              placement={'bottomRight'}
              arrowPointAtCenter
              title={tooltip_text}
            >
              <TooltipIcon />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="br-orders__no-cod-label">
      {fmt({
        id: `common.no_cash_on_delivery`
      })}
    </div>
  );
};

const formatLogs = (logsArray) => {
  let formattedLogs = [];
  logsArray.forEach((log) => {
    let logObject = {
      takenBy: formatTakenBy(log),
      logDate: dayjs(log.createdAt).format('ddd, D MMM, h:mm a'),
      description: log.description,
      tierName: log?.tier_name
    };
    switch (log.action) {
      case LOG_ACTIONS.availability:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.status_action'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.status_log_text'
            },
            {
              status: fmt({
                id: `business_details.api_integration.states.${
                  log.new_value === '1' ? 'enabled' : 'disabled'
                }`
              }),
              itemName: log.item_name
            }
          )
        });
        return;
      case LOG_ACTIONS.balance:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.quantity_action'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.quantity_log_text'
            },
            {
              oldValue: log.old_value,
              itemName: log.item_name,
              newValue: log.new_value
            }
          )
        });
        return;
      case LOG_ACTIONS.price:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.price_action'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.price_log_text'
            },
            {
              oldValue: `${log.old_value} ${getCurrency().type}`,
              itemName: log.item_name,
              newValue: `${log.new_value} ${getCurrency().type}`
            }
          )
        });
        return;
      case LOG_ACTIONS.outOfStock:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.out_of_stock_action'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.out_of_stock_text'
            },
            {
              oldValue: `${+log.old_value ? 'Out of Stock' : 'In Stock'}`,
              itemName: log.item_name,
              newValue: `${+log.new_value ? 'Out of Stock' : 'In Stock'}`
            }
          )
        });
        return;
      case LOG_ACTIONS.createItem:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.create_item'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.create_item_text'
            },
            {
              itemName: log.item_name
            }
          )
        });
        return;
      case LOG_ACTIONS.maxLimitValidation:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.max_limit_validation_updated'
          }),
          logInfo: fmt(
            {
              id: `packaging.manage_materials.logs.${
                log.new_value === '1'
                  ? 'max_limit_validation_enabled'
                  : 'max_limit_validation_disabled'
              }`
            },
            {
              itemName: log.item_name
            }
          )
        });
        return;
      case LOG_ACTIONS.maxLimitNumber:
        formattedLogs.push({
          ...logObject,
          logTitle: fmt({
            id: 'packaging.manage_materials.logs.max_limit_number_updated'
          }),
          logInfo: fmt(
            {
              id: 'packaging.manage_materials.logs.max_limit_number_text'
            },
            {
              itemName: log.item_name,
              oldValue: log.old_value,
              newValue: log.new_value
            }
          )
        });
        return;

      default:
        return;
    }
  });
  return formattedLogs;
};
let PAGE_SIZE = 20;
export const useMaterialLogs = (isPackagingPage = false) => {
  const [logs, setLogs] = useState([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  useEffect(() => {
    getLogs();
  }, []);

  const getLogs = async (customPageNumber = 0, isRefresh = false) => {
    setIsLoading(true);
    try {
      const { logsList, totalLogs } = await getMaterialLogs({
        pageNumber: customPageNumber,
        pageSize: PAGE_SIZE,
        payload: !isSaudi() && {
          actionTypes: isPackagingPage
            ? [
                'Update Availability',
                'Update Balance',
                'Update Out of Stock Status',
                'Create Item',
                'Update Max Limit Validation',
                'Update Max Limit Number'
              ]
            : ['Update Price']
        }
      });
      setLogs((prev) => {
        const formattedLogs = formatLogs(logsList);
        if (isRefresh) {
          return [...formattedLogs];
        }
        return [...prev, ...formattedLogs];
      });
      setShowLoadMore(totalLogs > (customPageNumber + 1) * PAGE_SIZE);
      customPageNumber !== pageNumber && setPageNumber(customPageNumber);
    } catch (error) {
      notify(error.message);
    }
    setIsLoading(false);
  };
  const loadMoreLogs = () => {
    getLogs(pageNumber + 1);
  };

  const refreshLogs = async () => {
    await getLogs(0, true);
  };

  return {
    logs,
    showLoadMore,
    isLoading,
    loadMoreLogs,
    haveManyLogs: logs.length > 1,
    refreshLogs
  };
};

export const formatAreaZone = (address) => {
  const { zone, district } = address;
  let formatedArea = '';

  if (!zone?.name && !district) {
    return fmt({
      id: `common.empty_field`
    });
  } else if (typeof district === 'string') {
    if (zone && (zone?.name === district || !district)) {
      formatedArea = `${zone?.name}`;
    } else {
      formatedArea = `${zone?.name} - ${district}`;
    }
  } else {
    formatedArea =
      zone && zone?.name !== district?.name
        ? district
          ? `${zone?.name} -${district?.name || ''}`
          : `${zone?.name}`
        : `${district?.name}`;
  }
  return formatedArea;
};

export const materialStatusLabel = (item) => {
  const { status, outOfStock } = item;

  if (!status) {
    return fmt({
      id: 'packaging.manage_materials.status.disabled'
    });
  }

  if (outOfStock) {
    return fmt({
      id: 'packaging.manage_materials.status.out_of_stock'
    });
  }

  return fmt({
    id: 'packaging.manage_materials.status.enabled'
  });
};

export const materialStatusColor = (item) => {
  const { status, outOfStock } = item;

  if (!status) {
    return 'gray';
  }

  if (outOfStock) {
    return 'red';
  }

  return 'green';
};
